/**
 * MeetingCalendar Component
 * Full calendar view for displaying customer meetings
 */

import { addMonths, format, getDay, parse, startOfWeek, subMonths } from "date-fns";
import { enUS } from "date-fns/locale";
import { useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, type View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { parseMeetingDateTime } from "@/lib/dateUtils";
import type { MeetingWithCustomer } from "@/types/meeting.types";
import "./calendar-custom.css";

const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: MeetingWithCustomer;
}

interface MeetingCalendarProps {
  meetings: MeetingWithCustomer[];
  onSelectEvent?: (meeting: MeetingWithCustomer) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  onDateChange?: (date: Date) => void;
  currentDate?: Date;
  isLoading?: boolean;
}

export function MeetingCalendar({
  meetings,
  onSelectEvent,
  onSelectSlot,
  onDateChange,
  currentDate,
  isLoading = false,
}: MeetingCalendarProps) {
  const [view, setView] = useState<View>("month");
  const [internalDate, setInternalDate] = useState(new Date());

  // Use controlled date if provided, otherwise use internal state
  const date = currentDate || internalDate;

  // Transform meetings into calendar events
  const events = useMemo<CalendarEvent[]>(() => {
    return meetings.map((meeting) => {
      const start = parseMeetingDateTime(meeting.meeting_date, meeting.meeting_time);
      const end = new Date(start.getTime() + meeting.duration_minutes * 60000);

      return {
        id: meeting.id,
        title: meeting.customer_name,
        start,
        end,
        resource: meeting,
      };
    });
  }, [meetings]);

  // Event styling based on meeting status and type
  const eventStyleGetter = (event: CalendarEvent) => {
    const meeting = event.resource;
    let backgroundColor = "#3b82f6"; // blue for scheduled
    let borderColor = "#2563eb";

    // Color by status
    if (meeting.status === "completed") {
      backgroundColor = "#10b981"; // green
      borderColor = "#059669";
    } else if (meeting.status === "cancelled") {
      backgroundColor = "#6b7280"; // gray
      borderColor = "#4b5563";
    } else if (meeting.status === "rescheduled") {
      backgroundColor = "#f59e0b"; // amber
      borderColor = "#d97706";
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "none",
        display: "block",
        fontSize: "0.875rem",
        padding: "2px 4px",
      },
    };
  };

  const handleNavigate = (newDate: Date) => {
    setInternalDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const handleViewChange = (newView: View) => {
    setView(newView);
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    if (onSelectEvent) {
      onSelectEvent(event.resource);
    }
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    if (onSelectSlot) {
      onSelectSlot(slotInfo);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading calendar...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="meeting-calendar-container">
      {/* Calendar Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* Month Navigation */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleNavigate(subMonths(date, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleNavigate(addMonths(date, 1))}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleNavigate(new Date())}>
            Today
          </Button>
          <span className="text-sm font-medium ml-2">{format(date, "MMMM yyyy")}</span>
        </div>

        {/* View Switcher */}
        <div className="flex gap-1">
          <Button
            variant={view === "month" ? "default" : "outline"}
            size="sm"
            onClick={() => handleViewChange("month")}
          >
            Month
          </Button>
          <Button
            variant={view === "week" ? "default" : "outline"}
            size="sm"
            onClick={() => handleViewChange("week")}
          >
            Week
          </Button>
          <Button
            variant={view === "day" ? "default" : "outline"}
            size="sm"
            onClick={() => handleViewChange("day")}
          >
            Day
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#3b82f6" }} />
          <span>Scheduled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#10b981" }} />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#6b7280" }} />
          <span>Cancelled</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: "#f59e0b" }} />
          <span>Rescheduled</span>
        </div>
      </div>

      {/* Calendar */}
      <Card>
        <CardContent className="p-4">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            view={view}
            onView={handleViewChange}
            date={date}
            onNavigate={handleNavigate}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable={true}
            eventPropGetter={eventStyleGetter}
            popup={true}
            views={["month", "week", "day"]}
            step={30}
            showMultiDayTimes={true}
            defaultView="month"
          />
        </CardContent>
      </Card>
    </div>
  );
}
